/**
 * @file ResetPasswordForm.jsx
 * @author Archana
 * @date 2024-10-25
 * @description Component manages the inputs and button / like form in Reset Password page.
 */
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { passwordRegex } from "../../../components/constants/regex";

export const CandidateResetPasswordForm = ({ onSubmit }) => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    watch,
  } = useForm();

  const newPassword = watch("newpassword", "");

  const evaluatePasswordStrength = (password) => {
    let strength = 0;

    if (password.length >= 8) strength += 25; // Length check
    if (/[A-Z]/.test(password)) strength += 25; // Uppercase letters
    if (/[0-9]/.test(password)) strength += 25; // Numbers
    if (/[^A-Za-z0-9]/.test(password)) strength += 25; // Special characters

    return strength;
  };

  const passwordStrength = evaluatePasswordStrength(newPassword);

  return (
    <Box
      component="form"
      maxWidth={400}
      width="100%"
      onSubmit={handleSubmit((data) => onSubmit(data, setError))}
    >
      <Stack spacing={3}>
        <Box>
          <TextField
            name="newpassword"
            label="New Password"
            type="password"
            fullWidth
            {...register("newpassword", {
              required: "Password is required",
              pattern: {
                value: passwordRegex,
                message: "Password does not meet the requirements.",
              },
            })}
            error={!!errors.newpassword}
            helperText={errors.newpassword ? errors.newpassword.message : ""}
          />

          {newPassword ? (
            <LinearProgress
              variant="determinate"
              value={passwordStrength}
              sx={{
                mt: 1.5,
                height: 8,
                borderRadius: 20,
                backgroundColor: "lightgray",
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    passwordStrength < 50
                      ? "red"
                      : passwordStrength < 75
                      ? "orange"
                      : "green",
                },
              }}
            />
          ) : (
            <Typography fontSize={12} color="#FA8232">
              Password needs uppercase, lowercase, numbers, and a special
              character.
            </Typography>
          )}
        </Box>

        <TextField
          name="confirmpassword"
          label="Confirm Password"
          type="password"
          fullWidth
          {...register("confirmpassword", {
            required: "Password is required",
            validate: (value) =>
              value === newPassword || "Passwords do not match.",
          })}
          error={!!errors.confirmpassword}
          helperText={
            errors.confirmpassword ? errors.confirmpassword.message : ""
          }
        />

        {errors.root && <Alert severity="error">{errors.root.message}</Alert>}

        <Button
          type="submit"
          size="large"
          variant="contained"
          disabled={isSubmitting}
          color="success"
        >
          Submit
        </Button>
      </Stack>
    </Box>
  );
};
